import { useState } from "react";
import { toast } from "sonner";
import CustomInquiryModal from "./CustomInquiryModal";
import ShareButton from "./ShareButton";

const BUCKED_UP_CODE = "FGM2026";
const BUCKED_UP_LINK = "https://bckd.co/uiErxCq";
const DEVOTED_BRAND_LINK = "https://thedevotedbrand.com/";

export default function Partners() {
    const [copied, setCopied] = useState(false);
    const [inquiryOpen, setInquiryOpen] = useState(false);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(BUCKED_UP_CODE);
            setCopied(true);
            toast.success(`Promo code ${BUCKED_UP_CODE} copied`);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy. Code: " + BUCKED_UP_CODE);
        }
    };

    return (
        <section
            id="partners"
            data-testid="partners-section"
            className="relative bg-fgm-dark py-16 md:py-24 overflow-hidden border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                {/* Section header */}
                <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <span className="h-px w-12 bg-[#E63946]" />
                            <span className="font-display tracking-[0.35em] text-zinc-400 text-sm uppercase">
                                / 05 — Partners
                            </span>
                        </div>
                        <h2
                            data-testid="partners-title"
                            className="font-display uppercase text-white text-5xl md:text-7xl leading-[0.9] tracking-tight"
                        >
                            Built With{" "}
                            <span className="font-outline-strong">Partners</span>
                            <span className="block text-[#E63946] text-3xl md:text-5xl mt-3 tracking-widest">
                                We Believe In
                            </span>
                        </h2>
                    </div>
                    <p className="text-zinc-400 max-w-sm font-body">
                        We move with brands that share the grind. From fuel to
                        custom apparel — these are the partners powering FGM.
                    </p>
                </div>

                {/* Partner cards grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* === BUCKED UP === */}
                    <article
                        data-testid="partner-card-buckedup"
                        className="group bg-fgm-surface border border-white/10 hover:border-[#E63946]/50 transition-colors flex flex-col"
                    >
                        <div className="relative aspect-[16/10] bg-gradient-to-br from-zinc-900 via-black to-zinc-900 flex items-center justify-center overflow-hidden p-10">
                            <ShareButton
                                eventLabel="partner-buckedup"
                                title="Bucked Up Energy × FGM Apparel"
                                text={`Fuel your grind with Bucked Up. Use code ${BUCKED_UP_CODE} for FGM-fam pricing.`}
                                path="#partners"
                                image="/brand/partners/buckedup-logo.png"
                                className="absolute top-3 left-3 z-10 h-8 w-8 flex items-center justify-center bg-black/80 backdrop-blur-md border border-white/20 hover:bg-[#E63946] hover:border-[#E63946] transition-colors"
                            />
                            <img
                                src="/brand/partners/buckedup-logo.png"
                                alt="Bucked Up Energy"
                                className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                                style={{ mixBlendMode: "screen" }}
                             loading="lazy" decoding="async" />
                            <div className="absolute top-3 right-3 bg-[#E63946] px-2.5 py-1">
                                <span className="font-display text-[10px] tracking-[0.3em] text-white uppercase">
                                    Affiliate
                                </span>
                            </div>
                        </div>

                        <div className="p-6 md:p-8 flex-1 flex flex-col">
                            <div className="flex items-start justify-between gap-4 mb-3">
                                <h3 className="font-display text-2xl md:text-3xl text-white uppercase leading-tight">
                                    Bucked Up Energy
                                </h3>
                                <span className="text-[10px] font-display tracking-[0.3em] text-[#E63946] uppercase mt-2 whitespace-nowrap">
                                    ● Brand Ambassador
                                </span>
                            </div>

                            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                                Pre-workout fuel built for athletes who train
                                like they mean it. We rep Bucked Up because they
                                push the same standard we do —{" "}
                                <span className="text-white">no shortcuts.</span>
                            </p>

                            {/* Promo code block */}
                            <div className="bg-black border border-white/10 p-4 mb-4">
                                <div className="font-display text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">
                                    Use FGM Promo Code
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                    <code className="font-display text-2xl md:text-3xl text-[#E63946] tracking-[0.15em]">
                                        {BUCKED_UP_CODE}
                                    </code>
                                    <button
                                        data-testid="partner-copy-code-buckedup"
                                        onClick={copyCode}
                                        className="font-display text-[10px] tracking-[0.3em] uppercase px-3 py-2 border border-white/20 text-white hover:bg-white hover:text-black transition-colors"
                                    >
                                        {copied ? "Copied ✓" : "Copy"}
                                    </button>
                                </div>
                            </div>

                            <a
                                data-testid="partner-shop-buckedup"
                                href={BUCKED_UP_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary w-full !py-3 !text-base mt-auto"
                            >
                                Shop Bucked Up →
                            </a>
                        </div>
                    </article>

                    {/* === DEVOTED BRAND / DTF === */}
                    <article
                        data-testid="partner-card-devoted"
                        className="group bg-fgm-surface border border-white/10 hover:border-white/30 transition-colors flex flex-col"
                    >
                        <div className="relative aspect-[16/10] bg-gradient-to-br from-zinc-900 via-black to-zinc-900 flex items-center justify-center overflow-hidden p-4 md:p-6">
                            <ShareButton
                                eventLabel="partner-devoted"
                                title="Custom DTF Printing × Devoted Brand"
                                text="Premium custom apparel + DTF printing for businesses, teams, and brands. Powered by Devoted Brand."
                                path="#partners"
                                image="/brand/partners/devoted-brand-wordmark.jpg"
                                className="absolute top-3 left-3 z-10 h-8 w-8 flex items-center justify-center bg-black/80 backdrop-blur-md border border-white/20 hover:bg-[#1D4ED8] hover:border-[#1D4ED8] transition-colors"
                            />
                            {/* Devoted Brand wordmark — inverted + screen blend strips white bg */}
                            <img
                                src="/brand/partners/devoted-brand-wordmark.jpg"
                                alt="Devoted Brand"
                                className="h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                                style={{ filter: "invert(1) contrast(1.15)", mixBlendMode: "screen" }}
                             loading="lazy" decoding="async" />
                            <div className="absolute top-3 right-3 bg-[#1D4ED8] px-2.5 py-1 z-10">
                                <span className="font-display text-[10px] tracking-[0.3em] text-white uppercase">
                                    Powered By
                                </span>
                            </div>
                        </div>

                        <div className="p-6 md:p-8 flex-1 flex flex-col">
                            <div className="flex items-start justify-between gap-4 mb-3">
                                <h3 className="font-display text-2xl md:text-3xl text-white uppercase leading-tight">
                                    Custom DTF Printing
                                </h3>
                                <span className="text-[10px] font-display tracking-[0.3em] text-[#1D4ED8] uppercase mt-2 whitespace-nowrap">
                                    ● Print Partner
                                </span>
                            </div>

                            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
                                Powered by{" "}
                                <span className="text-white">Devoted Brand</span>.
                                Premium custom apparel and DTF printing for
                                businesses, teams, and brands. From uniforms and
                                activewear to promotional apparel — bold,
                                durable prints built to represent your business
                                professionally.
                            </p>

                            <div className="bg-black border border-white/10 p-4 mb-4">
                                <div className="font-display text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">
                                    Services
                                </div>
                                <div className="text-sm text-zinc-300">
                                    Uniforms · Activewear · Promo apparel ·
                                    Team kits
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                                <button
                                    data-testid="partner-inquire-devoted"
                                    onClick={() => setInquiryOpen(true)}
                                    className="btn-primary flex-1 !py-3 !text-base"
                                >
                                    Submit Inquiry →
                                </button>
                                <a
                                    data-testid="partner-visit-devoted"
                                    href={DEVOTED_BRAND_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-outline flex-1 !py-3 !text-base text-center"
                                >
                                    Visit Site →
                                </a>
                            </div>
                        </div>
                    </article>
                </div>
            </div>

            <CustomInquiryModal
                open={inquiryOpen}
                onClose={() => setInquiryOpen(false)}
            />
        </section>
    );
}
